import { FREE_MONTHLY_CREDITS } from "../../../constants/credits";

const tool = {
  slug: "listing-grader",
  name: "Listing Checkup",
  title: "Free Listing Checkup: Find What's Holding a Listing Back",
  metaTitle: "Free Listing Checkup | Listino",
  description:
    "Check any real estate listing across price position, description, and photos — and get specific fixes for whatever's holding it back. Free from Listino.",
  status: "coming-soon",
  primaryCtaPath: "/get-started",
  tldr: "A free checkup of any listing across the three things buyers react to first: price position, description strength, and photo quality and order — with specific fixes for whatever's holding it back.",
  blocks: [
    { type: "h2", id: "what-it-checks", text: "What the Listing Checkup looks at" },
    {
      type: "p",
      spans: [
        "A listing that isn't performing is almost always weak in one of three areas. The Listing Checkup reviews each one and tells you where to start.",
      ],
    },
    {
      type: "table",
      headers: ["Lever", "What we check", "Why it matters"],
      rows: [
        [
          "Price position",
          "List price vs. recent comparable sales and active competition",
          "The most common reason a listing stalls",
        ],
        [
          "Description",
          "Whether it leads with a standout feature and sells the lifestyle",
          "Decides whether a click becomes a showing",
        ],
        [
          "Photos",
          "Lead-photo strength, count, and the order they're shown in",
          "Decides whether buyers click at all",
        ],
      ],
    },
    { type: "h2", id: "how-it-works", text: "How the checkup works" },
    {
      type: "p",
      spans: [
        "Each lever is reviewed independently. A home can have beautiful photos and still stall if it's priced above the market — and vice versa. The result isn't a number; it's a prioritized list of what's working, what's holding the listing back, and the specific fix for each.",
      ],
    },
    {
      type: "ul",
      items: [
        ["Market-ready: ", "everything's pulling its weight. Minor polish at most."],
        ["One clear opportunity: ", "solid overall, with a single fix worth making first."],
        [
          "Leaving interest on the table: ",
          "usually price position or the lead photo — the checkup names which.",
        ],
        [
          "Needs a reset: ",
          "several levers working against the listing at once. See ",
          { text: "how to relist a house", href: "/resources/how-to-relist-a-house" },
          ".",
        ],
      ],
    },
    { type: "h2", id: "fix-each-lever", text: "How to fix each lever" },
    {
      type: "p",
      spans: [
        "If your ",
        { text: "price position", href: "/resources/is-my-house-overpriced" },
        " is the problem, the fix is a single decisive move into the range of sold comps. If your ",
        { text: "description", href: "/resources/how-to-write-a-listing-description-that-sells" },
        " is weak, lead with the best feature instead of generic adjectives. If your ",
        { text: "photos", href: "/resources/best-order-for-listing-photos" },
        " are holding things back, fix the lead image and re-sequence the rest.",
      ],
    },
    { type: "h2", id: "free-vs-full", text: "The free checkup vs. a full listing prep" },
    {
      type: "p",
      spans: [
        "The Listing Checkup tells you where a listing stands and what to fix first. A full ",
        { text: "Listino listing prep", href: "/get-started" },
        " does the work for you: it rewrites the description, re-sequences and touches up the photos, and includes a comparative market analysis — so you're not just diagnosed, you're fixed.",
      ],
    },
  ],
  faqs: [
    {
      question: "Is the Listing Checkup really free?",
      answer: [
        {
          type: "p",
          spans: [
            `Yes — checking a listing is free. A full Listino listing prep is free to start as well: every account includes ${FREE_MONTHLY_CREDITS.toLocaleString("en-US")} credits a month, which covers a Listing Analysis with the rewritten description and a comparative market analysis. Photo touch-ups and exported files are priced separately in credits.`,
          ],
        },
      ],
    },
    {
      question: "What do I need to check a listing?",
      answer: [
        {
          type: "p",
          spans: [
            "Just the listing — a Zillow URL or the basic property details and photos. The checkup looks at the same things a buyer does.",
          ],
        },
      ],
    },
    {
      question: "Does a clean checkup guarantee a fast sale?",
      answer: [
        {
          type: "p",
          spans: [
            "No. A clean checkup means the listing is presented and priced competitively, which removes the most common obstacles — but market conditions always play a role. See our ",
            { text: "methodology", href: "/resources/methodology" },
            ".",
          ],
        },
      ],
    },
  ],
};

export default tool;
